import { formatInUTC } from '../../date'
import { BRAZE_FORM_FIELDS } from './constants'

export const USER_ATTRIBUTES_UPDATE_EVENT_NAME = 'UserAttributesUpdate'

/**
 * Braze date/time values → `yyyy-MM-dd` for HTML date inputs (UTC calendar day).
 * @param {unknown} raw
 * @returns {string}
 */
export function brazeAttributeToDateInputValue (raw) {
  if (raw === null || raw === undefined || raw === '') {
    return ''
  }
  const formatted = formatInUTC(raw, 'yyyy-MM-dd')
  return formatted || ''
}

/**
 * @param {unknown} raw
 * @param {{ inputType?: string }} field
 * @returns {string}
 */
export function attributeValueToFormString (raw, field = {}) {
  if (raw === null || raw === undefined) {
    return ''
  }
  if (field.inputType === 'date') {
    return brazeAttributeToDateInputValue(raw)
  }
  if (field.inputType === 'boolean') {
    if (raw === true || raw === 'true') {
      return 'true'
    }
    if (raw === false || raw === 'false') {
      return 'false'
    }
  }
  return String(raw)
}

/**
 * @param {Record<string, unknown> | null | undefined} attributes
 * @param {typeof BRAZE_FORM_FIELDS} [fields]
 * @returns {Record<string, string>}
 */
export function attributesToFormState (attributes, fields = BRAZE_FORM_FIELDS) {
  const state = {}
  if (!attributes || typeof attributes !== 'object') {
    return state
  }
  fields.forEach((field) => {
    state[field.brazeKey] = attributeValueToFormString(attributes[field.brazeKey], field)
  })
  return state
}

/**
 * @param {string} a
 * @param {string} b
 */
export function isFormValueDirty (a, b) {
  return (a ?? '') !== (b ?? '')
}

/**
 * @param {string} formValue
 * @param {{ inputType?: string, eventDataKey?: string }} field
 * @returns {unknown}
 */
export function formValueToEventData (formValue, field) {
  if (formValue === '' || formValue === undefined || formValue === null) {
    return undefined
  }
  if (field.inputType === 'boolean') {
    return formValue === 'true'
  }
  if (field.inputType === 'number') {
    const num = Number(formValue)
    return Number.isNaN(num) ? undefined : num
  }
  return formValue
}

/**
 * @param {Object} options
 * @param {string} options.externalId
 * @param {string} [options.email]
 * @param {Record<string, string>} options.formState
 * @param {Record<string, string>} options.baseline
 * @param {typeof BRAZE_FORM_FIELDS} [options.fields]
 * @param {boolean} [options.includeAllDirty]
 */
export function buildUserAttributesUpdateInput ({
  externalId,
  email,
  formState,
  baseline,
  fields = BRAZE_FORM_FIELDS,
  includeAllDirty = false,
}) {
  const data = {}
  fields.forEach((field) => {
    if (!field.saveable || !field.eventDataKey) {
      return
    }
    const current = formState[field.brazeKey]
    const base = baseline[field.brazeKey]
    if (!includeAllDirty && !isFormValueDirty(current, base)) {
      return
    }
    const converted = formValueToEventData(current, field)
    if (converted !== undefined) {
      data[field.eventDataKey] = converted
    }
  })

  if (email) {
    data.email = email
  }
  data.externalId = externalId

  return {
    eventName: USER_ATTRIBUTES_UPDATE_EVENT_NAME,
    data,
  }
}

/**
 * @param {string} query
 * @param {{ label: string, brazeKey: string }} field
 */
export function fieldMatchesSearch (field, query) {
  if (!query || !String(query).trim()) {
    return true
  }
  const q = String(query).trim().toLowerCase()
  return field.label.toLowerCase().includes(q) || field.brazeKey.toLowerCase().includes(q)
}

/**
 * @param {typeof BRAZE_FORM_FIELDS} fields
 * @param {Object} options
 * @param {string} [options.search]
 * @param {boolean} [options.onlyChanged]
 * @param {Record<string, string>} options.formState
 * @param {Record<string, string>} options.baseline
 */
export function filterVisibleFormFields (fields, options) {
  const { search, onlyChanged, formState, baseline } = options
  return fields.filter((field) => {
    if (field.hiddenInForm) {
      return false
    }
    if (!fieldMatchesSearch(field, search)) {
      return false
    }
    if (onlyChanged && !isFormValueDirty(formState[field.brazeKey], baseline[field.brazeKey])) {
      return false
    }
    return true
  })
}

/**
 * @param {Record<string, string>} formState
 * @param {Record<string, string>} baseline
 * @param {typeof BRAZE_FORM_FIELDS} [fields]
 */
export function hasDirtyFormFields (formState, baseline, fields = BRAZE_FORM_FIELDS) {
  return fields.some(
    (field) =>
      field.saveable && isFormValueDirty(formState[field.brazeKey], baseline[field.brazeKey]),
  )
}
